/**
 * =============================================================================
 * REPLY IMAGE STRIP (admin per-reply image context)
 * =============================================================================
 *
 * Per-reply thumbnail strip + moderation status badge for the admin reply
 * moderation view (ADR-022 §14 D-ADMIN-REPLY-DISPLAY / D5). Renders the images
 * attached to ONE reply, in EVERY moderation state (the reply-list route feeds
 * it from the UNFILTERED admin batch read — never the §6.1 public gate), so a
 * moderator gets per-reply context without leaving the question detail modal.
 *
 * Self-contained + RELOCATABLE: it owns nothing but its `images` + `token`, so
 * ADR-025's future routed workspace can re-home it unchanged. Each thumbnail is
 * an `AdminImageThumbnail` (authenticated blob via `/api/admin/images/:id` —
 * never a raw `<img src>`), keyed by `attachmentId`, with a `MediaStatusBadge`
 * and, when set, a legal-hold indicator (surface the axis; write protections are
 * enforced elsewhere and never weakened here).
 *
 * Bounded fan-out: `AdminImageThumbnail` fetches on mount, and a page can hold
 * up to (page-size × per-reply-cap) images, so only the first `INITIAL_VISIBLE`
 * per reply mount initially; the rest are behind an explicit "Show N more" that
 * mounts them on demand. The bound is a PER-REPLY constant (independent of the
 * page size), and each strip's expansion is local — expanding one reply never
 * fetches for another.
 *
 * =============================================================================
 */

import React, { useCallback, useState } from 'react';
import { Badge, Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import { AccessibleModal } from '../common/AccessibleModal';
import type { AdminMediaListItem } from '@find-my-part/types';
import { AdminImageThumbnail } from '../Media/AdminImageThumbnail';
import { MediaStatusBadge } from '../Media/MediaStatusBadge';

/** Thumbnails mounted (and thus fetched) per reply before "Show N more". */
const INITIAL_VISIBLE = 4;
/** Square thumbnail edge in the compact reply strip. */
const THUMB_SIZE = 72;

export interface ReplyImageStripProps {
  /** The reply's images (admin shape — every moderation state). */
  images: AdminMediaListItem[];
  /** BigCommerce signed_payload_jwt for the authenticated blob fetch. */
  token: string;
  /** Optional test-id prefix. */
  testId?: string;
}

/**
 * Per-reply admin image strip. Renders nothing when the reply has no images.
 */
export function ReplyImageStrip({
  images,
  token,
  testId,
}: ReplyImageStripProps): React.ReactElement | null {
  const [expanded, setExpanded] = useState(false);
  // The open lightbox carries a per-image LABEL (positional + filename) so each
  // preview is identified distinctly (WCAG 2.2 SC 1.1.1) — not a shared
  // "Full-size preview" for every image.
  const [preview, setPreview] = useState<{ url: string; label: string } | null>(null);
  // Focus restoration is owned by A-0 (`AccessibleModal` captures the trigger
  // focused at open and restores it on the open→closed transition) — the
  // bespoke triggerRef/effect this component carried pre-B-6 is retired
  // (S-1 memo §6.2; design-v3 §D.3).
  const openPreview = useCallback((url: string, label: string): void => {
    setPreview({ url, label });
  }, []);

  const closePreview = useCallback((): void => {
    setPreview(null);
  }, []);

  if (images.length === 0) {
    return null;
  }

  const visible = expanded ? images : images.slice(0, INITIAL_VISIBLE);
  const hiddenCount = images.length - visible.length;

  return (
    <Box marginBottom="small" data-testid={testId}>
      <Text color="secondary" bold>
        Reply images ({images.length})
      </Text>
      <Flex
        flexWrap="wrap"
        flexGap="0.75rem"
        marginTop="xSmall"
        role="group"
        aria-label={`Images attached to this reply (${images.length})`}
      >
        {visible.map((image, index) => {
          // Distinct per-image label: positional + filename (fallback to the id).
          // Used BOTH as the thumbnail's accessible name and the lightbox identity
          // so they match (WCAG 2.2 SC 1.1.1). `index` is the position in the
          // images array (visible is a prefix slice), so it is stable across
          // expand/collapse.
          const label = `Photo ${index + 1} — ${image.originalFilename ?? image.attachmentId}`;
          return (
          <FlexItem key={image.attachmentId}>
            <Flex flexDirection="column" flexGap="4px" alignItems="center">
              <AdminImageThumbnail
                src={image.url}
                alt={label}
                token={token}
                size={THUMB_SIZE}
                onClick={(blobUrl) => openPreview(blobUrl, label)}
                testId={testId ? `${testId}-thumb-${image.attachmentId}` : undefined}
              />
              <MediaStatusBadge
                status={image.moderationStatus}
                data-testid={testId ? `${testId}-status-${image.attachmentId}` : undefined}
              />
              {image.legalHold && (
                <Badge
                  variant="danger"
                  label="Legal hold"
                  aria-label="Preserved under legal hold"
                  data-testid={testId ? `${testId}-hold-${image.attachmentId}` : undefined}
                />
              )}
            </Flex>
          </FlexItem>
          );
        })}
      </Flex>

      {hiddenCount > 0 && (
        <Box marginTop="xSmall">
          <Button
            variant="subtle"
            onClick={() => setExpanded(true)}
            data-testid={testId ? `${testId}-show-more` : undefined}
          >
            Show {hiddenCount} more
          </Button>
        </Box>
      )}

      {/* B-6 retrofit (M11/M19): KEEP-MOUNTED AccessibleModal — A-0's focus
          restore fires on the observed isOpen true→false transition, which the
          old mount-as-open pattern skipped. The fallback header string is never
          rendered (a closed big-design Modal renders no portal); it satisfies
          A-0's named-dialog invariant. The dialog stays nested under the mounted
          strip: `preview.url` is a lease held by the originating
          AdminImageThumbnail (the dialog holds none). */}
      <AccessibleModal
        isOpen={preview !== null}
        onClose={closePreview}
        closeOnEscKey
        closeOnClickOutside
        header={preview ? `Reply image — ${preview.label}` : 'Reply image'}
        data-testid={testId ? `${testId}-preview-modal` : undefined}
      >
        {preview && (
          <Box style={{ textAlign: 'center' }}>
            <img
              src={preview.url}
              alt={preview.label}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Box>
        )}
      </AccessibleModal>
    </Box>
  );
}
